import React, { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { useI18n } from "@/i18n/I18nProvider";
import { useSettings } from "@/context/SettingsContext";
import { Eyebrow } from "@/components/ui-kit/Kit";
import { Reveal } from "@/components/motion/Reveal";

const Counter = ({ value = 0, pad = 3, label, testid, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView || !value) return;
    let frame;
    const start = performance.now();
    const dur = 1400;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, value]);

  return (
    <div ref={ref} className={`group relative border-t border-white/12 pt-6 ${className}`} data-testid={testid}>
      <div className="flex items-baseline gap-1">
        <span className="font-display text-[clamp(2.4rem,4.6vw,3.9rem)] font-bold leading-none tracking-tight text-white">
          {String(n).padStart(pad, "0")}
        </span>
        <span className="font-display text-2xl font-light text-gold">+</span>
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-navy-300">{label}</p>
      <span className="absolute left-0 top-0 h-[1px] w-0 bg-gold transition-[width] duration-700 group-hover:w-full" />
    </div>
  );
};

export const Counters = () => {
  const { t } = useI18n();
  const { settings } = useSettings();
  const c = settings?.counters || {};

  const items = [
    { key: "verified_members", label: t("home.counters.members"), pad: 3, testid: "counter-members" },
    { key: "verified_vendors", label: t("home.counters.vendors"), pad: 3, testid: "counter-vendors" },
    { key: "districts_covered", label: t("home.counters.districts"), pad: 2, testid: "counter-districts" },
    { key: "committee_members", label: t("home.counters.committee"), pad: 2, testid: "counter-committee" },
    { key: "support_resolved", label: t("home.counters.support"), pad: 3, testid: "counter-support", className: "col-span-2 sm:col-span-1" },
  ];

  return (
    <section className="relative overflow-hidden bg-navy py-20 text-white grain sm:py-24" data-testid="counters-section">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 hairline-grid" />
      <div className="relative container">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <Eyebrow tone="gold">{t("home.counters.title")}</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-xl text-[12px] leading-relaxed text-white/45">{t("home.counters.note")}</p>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 lg:grid-cols-5">
          {items.map((it) => (
            <Counter key={it.key} value={c[it.key] || 0} pad={it.pad} label={it.label} testid={it.testid} className={it.className} />
          ))}
        </div>
      </div>
    </section>
  );
};
